/* TAZTIR Stories V5 — scoped enhancement, GSAP + ScrollTrigger, no scroll interception.
 * Content is visible before JS. Every component owns its listeners and triggers.
 * Native links, details, checkboxes and the horizontal rail also work without GSAP.
 */
(() => {
  'use strict';
  if (customElements.get('taztir-story')) return;
  const loads = new Map();
  function loadScript(src, ready) {
    if (ready()) return Promise.resolve();
    if (loads.has(src)) return loads.get(src);
    const promise = new Promise((resolve, reject) => {
      const existing = [...document.scripts].find(s => s.src === new URL(src, document.baseURI).href);
      const script = existing || document.createElement('script');
      let timer;
      const finish = () => { clearTimeout(timer); ready() ? resolve() : reject(new Error('Animation library unavailable')); };
      script.addEventListener('load', finish, {once:true});
      script.addEventListener('error', () => {clearTimeout(timer);reject(new Error('Animation library failed'));}, {once:true});
      timer = setTimeout(() => ready() ? resolve() : reject(new Error('Animation library timeout')), 12000);
      if (!existing) {script.src=src;script.async=true;document.head.append(script);}
    });
    loads.set(src,promise);return promise;
  }
  async function libraries(root) {
    await loadScript(root.dataset.gsap, () => !!window.gsap);
    await loadScript(root.dataset.scrolltrigger, () => !!window.ScrollTrigger);
    window.gsap.registerPlugin(window.ScrollTrigger);
  }
  class TaztirStory extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.railJobs=new Map();this.timers=new Set();this.frames=new Set();
      this.initialFrame=requestAnimationFrame(() => this.init());
    }
    disconnectedCallback() {
      this.railJobs?.forEach(id=>cancelAnimationFrame(id));this.timers?.forEach(id=>clearTimeout(id));this.frames?.forEach(id=>cancelAnimationFrame(id));
      cancelAnimationFrame(this.initialFrame);this.abort?.abort();this.abort=null;
      this.media?.revert();this.media=null;this.resize?.disconnect();this.resize=null;this.homeResize?.disconnect();this.homeResize=null;this.dayObserver?.disconnect();this.dayObserver=null;this.extraTweens?.forEach(t=>t.kill());this.extraTweens=null;
      this.querySelectorAll('[data-panel]').forEach(p=>p.hidden=false);
    }
    on(node,type,fn,options={}) {node.addEventListener(type,fn,{...options,signal:this.abort.signal});}
    async init() {
      if (!this.isConnected || !this.abort) return;
      const session=this.abort;
      this.on(matchMedia("(prefers-reduced-motion: reduce)"),"change",()=>{this.railJobs.forEach(id=>cancelAnimationFrame(id));this.railJobs.clear();});
      this.images();this.selectors();this.gallery();this.homeDeck();this.dayStory();this.notes();this.pointer();this.dragRail();this.reasons();
      this.on(this,'focusin',event=>{const el=event.target.closest('[data-reveal]');if(el&&window.gsap){window.gsap.killTweensOf(el);window.gsap.set(el,{opacity:1,x:0,y:0,clipPath:'inset(0 0% 0 0)'});}});
      this.on(this,'toggle',()=>window.ScrollTrigger?.refresh(),{capture:true});
      if(this.dataset.motion!=='true'){this.dataset.motionReady='disabled';return;}
      try {
        await libraries(this);
        if(!this.isConnected || this.abort!==session) return;
        this.motion();this.dataset.motionReady='true';
        const refresh=()=>{if(this.isConnected)window.ScrollTrigger.refresh();};
        this.querySelectorAll('img').forEach(img=>{if(!img.complete)this.on(img,'load',refresh,{once:true});});
        document.fonts?.ready.then(refresh);
      } catch (_) {
        // Do not conceal or disable content when a dependency is blocked.
        this.dataset.motionReady='fallback';
      }
    }
    selectors() {
      this.querySelectorAll('[data-selector]').forEach(group=>{
        const tabs=group.querySelector('[data-tabs]');
        const buttons=[...group.querySelectorAll('[data-choice]')];
        const panels=[...group.querySelectorAll('[data-panel]')];
        if(!tabs || buttons.length!==panels.length || !panels.length)return;
        tabs.hidden=false;tabs.setAttribute('role','tablist');
        let current=0;
        const activate=(index,focus=false,animate=true)=>{
          current=(index+panels.length)%panels.length;
          buttons.forEach((button,i)=>{button.setAttribute('aria-selected',String(i===current));button.tabIndex=i===current?0:-1;});
          panels.forEach((panel,i)=>{
            panel.hidden=i!==current;
            if(i===current){
              panel.querySelectorAll("img").forEach(img=>img.loading="eager");
              // A tab may have been outside its scroll range before selection.
              if(window.gsap){window.gsap.killTweensOf(panel);window.gsap.set(panel,{clearProps:'opacity,transform,clipPath'});}
              if(animate && window.gsap && this.dataset.motion==='true' && !matchMedia('(prefers-reduced-motion: reduce)').matches){
                window.gsap.fromTo(panel,{opacity:.35,clipPath:'inset(0 2% 0 0)'},{opacity:1,clipPath:'inset(0 0% 0 0)',duration:.45,ease:'expo.out',clearProps:'opacity,clipPath'});
              }
            }
          });
          if(focus)buttons[current].focus();
          window.ScrollTrigger?.refresh();
        };
        buttons.forEach((button,i)=>{
          button.id ||= panels[i].id+'-tab';button.setAttribute('role','tab');
          panels[i].setAttribute('role','tabpanel');panels[i].setAttribute('aria-labelledby',button.id);
          this.on(button,'click',()=>activate(i));
          this.on(button,'keydown',event=>{
            let n=current;
            if(event.key==='ArrowRight')n++;else if(event.key==='ArrowLeft')n--;else if(event.key==='Home')n=0;else if(event.key==='End')n=panels.length-1;else return;
            event.preventDefault();activate(n,true);
          });
        });
        this.on(this,'shopify:block:select',event=>{const panel=event.target.closest('[data-panel]');const i=panels.indexOf(panel);if(i>=0)activate(i,false,false);});
        activate(0,false,false);
      });
    }
    gallery() {
      this.querySelectorAll('[data-gallery]').forEach(gallery=>{
        const rail=gallery.querySelector('[data-rail]'),frames=[...rail.children];
        const controls=gallery.querySelector('.tz2-gallery-controls');
        if(!controls || !frames.length)return;
        controls.hidden=false;
        const prev=controls.querySelector('[data-prev]'),next=controls.querySelector('[data-next]'),count=controls.querySelector('[data-count]');
        let current=0,raf;
        const update=()=>{
          const origin=rail.getBoundingClientRect().left+parseFloat(getComputedStyle(rail).paddingLeft);
          let distance=Infinity;
          frames.forEach((frame,i)=>{const d=Math.abs(frame.getBoundingClientRect().left-origin);if(d<distance){distance=d;current=i;}});
          count.textContent=(current+1)+' / '+frames.length;
          prev.disabled=rail.scrollLeft<2;next.disabled=rail.scrollLeft+rail.clientWidth>=rail.scrollWidth-3;
        };
        const go=direction=>{
          const target=frames[Math.max(0,Math.min(frames.length-1,current+direction))];
          const origin=rail.getBoundingClientRect().left+parseFloat(getComputedStyle(rail).paddingLeft);
          this.scrollRail(rail,rail.scrollLeft+target.getBoundingClientRect().left-origin);
        };
        this.on(prev,'click',()=>go(-1));this.on(next,'click',()=>go(1));
        this.on(rail,'scroll',()=>{cancelAnimationFrame(raf);raf=requestAnimationFrame(update);},{passive:true});
        this.on(rail,'keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();go(e.key==='ArrowRight'?1:-1);}});
        this.on(this,'shopify:block:select',e=>{const frame=e.target.closest('.tz2-frame');if(frame){current=frames.indexOf(frame);go(0);}});
        this.resize=new ResizeObserver(update);this.resize.observe(rail);update();
      });
    }
    /* V3: each enhancement owns its events through the component AbortController. */
    /* V4: dwell prevents accidental accordion changes; touch remains free-scrolling. */
    /* V5：输入立即选中，CSS 负责柔和位移。手机选中项按实际可见面积同步。 */
    homeDeck() {
      this.querySelectorAll('[data-home-deck]').forEach(deck=>{
        const cards=[...deck.querySelectorAll('[data-home-story]')],buttons=[...deck.querySelectorAll('[data-home-choice]')],rail=deck.querySelector('[data-home-rail]');
        if(this.dataset.interaction==='false'||!cards.length)return;
        deck.dataset.homeReady='true';if(buttons[0])buttons[0].parentElement.hidden=false;
        const mobile=matchMedia('(max-width:749px)');let current=-1,requested=null,hover,hoverIndex=-1,frame;
        const clear=()=>{clearTimeout(hover);this.timers.delete(hover);hoverIndex=-1;};
        const mark=i=>{
          if(i<0||i>=cards.length)return;
          current=i;deck.dataset.activeStory=String(i);
          cards.forEach((c,n)=>c.classList.toggle('is-active',i===n));
          buttons.forEach((b,n)=>b.setAttribute('aria-pressed',String(i===n)));
        };
        const sync=()=>{
          if(!mobile.matches||requested!==null)return;
          const r=rail.getBoundingClientRect(),end=rail.scrollWidth-rail.clientWidth;
          if(rail.scrollLeft<2){mark(0);return;}
          if(end-rail.scrollLeft<2){mark(cards.length-1);return;}
          let best=-1,winner=0;
          cards.forEach((card,i)=>{const c=card.getBoundingClientRect();const visible=Math.max(0,Math.min(c.right,r.right)-Math.max(c.left,r.left));if(visible>best){best=visible;winner=i;}});
          mark(winner);
        };
        const go=i=>{
          clear();mark(i);
          if(mobile.matches){
            requested=i;
            const left=rail.scrollLeft+cards[i].getBoundingClientRect().left-rail.getBoundingClientRect().left-parseFloat(getComputedStyle(rail).paddingLeft);
            this.scrollRail(rail,left,480,()=>{requested=null;sync();});
          }
        };
        buttons.forEach((b,i)=>{
          this.on(b,'click',()=>go(i));
          this.on(b,'keydown',e=>{let next;if(e.key==='ArrowRight')next=(i+1)%cards.length;if(e.key==='ArrowLeft')next=(i+cards.length-1)%cards.length;if(e.key==='Home')next=0;if(e.key==='End')next=cards.length-1;if(next!==undefined){e.preventDefault();buttons[next].focus();go(next);}});
        });
        // Real pointer movement, not layout-induced pointerenter, changes the desktop card.
        this.on(deck,'pointermove',e=>{
          if(e.pointerType!=='mouse'||mobile.matches||deck.querySelector('[data-home-story] :focus-visible'))return;
          const card=e.target.closest('[data-home-story]');if(!card)return;const i=cards.indexOf(card);
          if(i===current){clear();return;}
          const delay=this.dataset.homeResponse==='custom'?Number(this.dataset.hoverDelay||0):0;
          if(!delay){clear();mark(i);return;}
          if(hoverIndex===i)return;clear();hoverIndex=i;hover=setTimeout(()=>{this.timers.delete(hover);mark(i);hoverIndex=-1;},delay);this.timers.add(hover);
        },{passive:true});
        this.on(deck,'pointerleave',clear);
        cards.forEach((c,i)=>this.on(c,'focusin',()=>go(i)));
        const interrupt=()=>{requested=null;clear();};
        this.on(rail,'pointerdown',interrupt,{passive:true});this.on(rail,'wheel',interrupt,{passive:true});this.on(rail,'keydown',interrupt);
        this.on(rail,'scroll',()=>{cancelAnimationFrame(frame);this.frames.delete(frame);frame=requestAnimationFrame(()=>{this.frames.delete(frame);sync();});this.frames.add(frame);},{passive:true});
        this.homeResize=new ResizeObserver(sync);this.homeResize.observe(rail);
        this.on(mobile,'change',()=>{requested=null;this.stopRail(rail);sync();});
        this.on(this,'shopify:block:select',e=>{const card=e.target.closest('[data-home-story]');if(card)go(cards.indexOf(card));});
        mark(0);sync();
      });
    }
    /* Native details remains usable without JavaScript. Opening a reason closes its peers. */
    reasons(){
      this.querySelectorAll('[data-reasons]').forEach(group=>{
        const items=[...group.querySelectorAll('[data-reason]')];
        items.forEach((item,i)=>this.on(item,'toggle',()=>{
          if(!item.open)return;group.dataset.activeReason=String(i);
          items.forEach(other=>{if(other!==item)other.open=false;});
        }));
      });
    }
    dayStory() {
      const panels=[...this.querySelectorAll('[data-day-panel]')],links=[...this.querySelectorAll('[data-day-link]')];
      if(!panels.length||this.dataset.interaction==='false')return;
      const mark=i=>links.forEach((a,n)=>{if(n===i)a.setAttribute('aria-current','step');else a.removeAttribute('aria-current');});
      this.dayObserver=new IntersectionObserver(entries=>{for(const entry of entries)if(entry.isIntersecting)mark(panels.indexOf(entry.target));},{rootMargin:'-20% 0px -45% 0px',threshold:0});
      panels.forEach(p=>this.dayObserver.observe(p));links.forEach((a,i)=>this.on(a,'click',()=>mark(i)));mark(0);
    }
    notes() {
      this.querySelectorAll('[data-gift-note]').forEach(note=>{
        const input=note.querySelector('[data-note-input]'),copy=note.querySelector('[data-note-copy]');
        if(!input||!copy)return;
        this.on(input,'input',()=>{copy.textContent=input.value;});
        // Native details remains the disclosure. No message is transmitted or persisted.
      });
    }
    pointer() {
      if(this.dataset.interaction==='false')return;
      this.on(this,'pointermove',e=>{
        if(e.pointerType!=='mouse'||matchMedia('(prefers-reduced-motion:reduce)').matches)return;
        const rect=this.getBoundingClientRect();this.style.setProperty('--tz-pointer-x',Math.max(0,Math.min(100,((e.clientX-rect.left)/rect.width)*100))+'%');
      },{passive:true});
      this.on(this,'pointerleave',()=>this.style.removeProperty('--tz-pointer-x'));
    }
    /* V4: mouse-only inertia. Native touch/trackpad physics are never intercepted. */
    stopRail(rail){cancelAnimationFrame(this.railJobs.get(rail));this.railJobs.delete(rail);}
    scrollRail(rail,left,duration=520,onComplete){
      this.stopRail(rail);const start=rail.scrollLeft,target=Math.max(0,Math.min(rail.scrollWidth-rail.clientWidth,left));
      if(matchMedia('(prefers-reduced-motion: reduce)').matches){rail.scrollLeft=target;onComplete?.();return;}
      const time=performance.now();const frame=now=>{if(!this.isConnected)return;const t=Math.min(1,(now-time)/duration),ease=1-Math.pow(1-t,3);rail.scrollLeft=start+(target-start)*ease;if(t<1)this.railJobs.set(rail,requestAnimationFrame(frame));else {this.railJobs.delete(rail);onComplete?.();}};
      this.railJobs.set(rail,requestAnimationFrame(frame));
    }
    dragRail(){
      if(this.dataset.interaction==='false')return;
      this.querySelectorAll('[data-drag-rail]').forEach(rail=>{
        let start=0,origin=0,lastX=0,lastTime=0,velocity=0,down=false,moved=false,swallow=false,pointer,touchStart=null,touchMoved=false;
        this.on(rail,'wheel',()=>this.stopRail(rail),{passive:true});
        this.on(rail,'keydown',()=>this.stopRail(rail),{capture:true});
        this.on(rail,'pointerdown',e=>{
          this.stopRail(rail);swallow=false;touchMoved=false;touchStart=e.pointerType==='mouse'?null:{x:e.clientX,y:e.clientY};
          if(e.pointerType!=='mouse'||e.button!==0||e.target.closest('button,input,textarea,select')||rail.scrollWidth<=rail.clientWidth+2)return;
          start=lastX=e.clientX;origin=rail.scrollLeft;lastTime=performance.now();velocity=0;down=true;moved=false;pointer=e.pointerId;
        });
        this.on(rail,'dragstart',e=>{if(down)e.preventDefault();});
        this.on(rail,'pointermove',e=>{
          if(touchStart&&e.pointerType!=='mouse'){if(Math.hypot(e.clientX-touchStart.x,e.clientY-touchStart.y)>12)touchMoved=true;return;}
          if(!down||e.pointerId!==pointer)return;const now=performance.now(),distance=e.clientX-start;
          if(!moved&&Math.abs(distance)>6){moved=true;rail.setPointerCapture(pointer);rail.classList.add('is-dragging');}
          if(moved){e.preventDefault();rail.scrollLeft=origin-distance;const dt=Math.max(8,now-lastTime);velocity=.3*velocity+.7*((lastX-e.clientX)/dt);lastX=e.clientX;lastTime=now;}
        });
        const finish=e=>{
          if(!down)return;down=false;rail.classList.remove('is-dragging');swallow=moved;
          if(rail.hasPointerCapture(pointer))rail.releasePointerCapture(pointer);
          if(!moved||e.type!=='pointerup'||performance.now()-lastTime>100||matchMedia('(prefers-reduced-motion:reduce)').matches)return;
          velocity=Math.max(-3,Math.min(3,velocity));let previous=performance.now();
          const glide=now=>{if(!this.isConnected)return;const dt=Math.max(8,Math.min(32,now-previous));previous=now;const old=rail.scrollLeft;rail.scrollLeft+=velocity*dt;velocity*=Math.exp(-dt/210);if(Math.abs(velocity)>.018&&Math.abs(rail.scrollLeft-old)>.1)this.railJobs.set(rail,requestAnimationFrame(glide));else this.railJobs.delete(rail);};
          this.railJobs.set(rail,requestAnimationFrame(glide));
        };
        this.on(rail,'pointerup',finish);this.on(rail,'pointercancel',finish);this.on(rail,'lostpointercapture',finish);
        this.on(rail,'pointerleave',e=>{if(down&&!moved)finish(e);});
        this.on(rail,'click',e=>{if(swallow||touchMoved){e.preventDefault();e.stopPropagation();swallow=false;touchMoved=false;}},{capture:true});
      });
    }
    /* Same-slot fallback only. A missing picture never silently borrows another scene. */
    images(){
      this.querySelectorAll('picture img').forEach(img=>{
        const failed=()=>{
          const picture=img.closest('picture'),fallback=img.dataset.defaultSrc;
          if(fallback&&!img.dataset.fallbackTried){img.dataset.fallbackTried='true';picture.querySelectorAll('source').forEach(s=>s.removeAttribute('srcset'));img.removeAttribute('srcset');img.src=fallback;img.loading='eager';return;}
          picture.classList.add('tz4-image-failed');picture.dataset.imageMessage=this.dataset.editor==='true'?'Image unavailable. Upload this slot’s picture in the theme editor. '+(img.dataset.asset||''):'This scene is temporarily unavailable.';
          picture.setAttribute('role','img');picture.setAttribute('aria-label',picture.dataset.imageMessage);img.hidden=true;
        };
        this.on(img,'error',failed);this.on(img,'load',()=>{img.hidden=false;const p=img.closest('picture');p.classList.remove('tz4-image-failed');p.removeAttribute('role');p.removeAttribute('aria-label');});
        if(img.complete&&img.naturalWidth===0)failed();
      });
    }

    motion() {
      const gsap=window.gsap,ST=window.ScrollTrigger;
      this.media=gsap.matchMedia();
      this.media.add('(prefers-reduced-motion: no-preference)',()=>{
        const distance=Number(this.dataset.distance)||28;
        const elements=[...this.querySelectorAll('[data-reveal]')];
        const tweens=new Set();
        const run=(el,vars)=>{const tween=gsap.to(el,{overwrite:true,...vars,onComplete:()=>tweens.delete(tween)});tweens.add(tween);};
        elements.forEach((el,index)=>{
          // Hidden inactive panels are animated by the selector. Their group remains scroll-aware.
          if(el.hidden)return;
          const kind=el.dataset.reveal;
          const enter=direction=>{
            if(el.hidden)return;
            const x=kind==='side'?distance*(index%2?1:-1):0;
            gsap.set(el,{opacity:.12,x,y:kind==='side'?0:direction*distance,clipPath:kind==='media'?'inset(0 2% 0 0)':'none'});
            run(el,{opacity:1,x:0,y:0,clipPath:'inset(0 0% 0 0)',duration:kind==='media'?.75:.62,ease:'expo.out'});
          };
          const leave=direction=>{
            // Keyboard navigation must never land on a visually concealed control.
            if(el.contains(document.activeElement))return;
            run(el,{opacity:0,x:0,y:-direction*distance*.55,duration:.24,ease:'power1.in'});
          };
          const rect=el.getBoundingClientRect();
          if(rect.top>=innerHeight*.94 || rect.bottom<=0)gsap.set(el,{opacity:0});
          ST.create({trigger:el,start:'top 94%',end:'bottom 2%',onEnter:()=>enter(1),onEnterBack:()=>enter(-1),onLeave:()=>leave(1),onLeaveBack:()=>leave(-1),invalidateOnRefresh:true});
        });
        this.querySelectorAll('[data-seam]').forEach(el=>{
          gsap.fromTo(el,{backgroundPosition:'100% 0',scaleX:.12},{backgroundPosition:'0% 0',scaleX:1,ease:'none',scrollTrigger:{trigger:el,start:'top 96%',end:'top 20%',scrub:.5}});
        });
        if(matchMedia('(min-width: 750px)').matches){
          this.querySelectorAll('[data-parallax]').forEach(el=>gsap.fromTo(el,{yPercent:-.5,scale:1.01},{yPercent:.5,scale:1.01,ease:'none',scrollTrigger:{trigger:this,start:'top top',end:'bottom top',scrub:.6}}));
        }
        return ()=>{tweens.forEach(t=>t.kill());elements.forEach(el=>gsap.set(el,{clearProps:'opacity,transform,clipPath'}));};
      },this);
    }
  }
  customElements.define('taztir-story',TaztirStory);
})();
